import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Features from "../../components/landingPage/features";
import PeakScore from "../../components/landingPage/peakScore";
import FloatingHeader from "../../components/landingPage/floatingMenu";
import Network from "../../components/landingPage/network";
import Intercom from "@intercom/messenger-js-sdk";
import { safeGetItem, safeSetItem } from "../../utils/localStorageUtils";
import Users from "../../components/landingPage/users";
import Logos from "../../components/landingPage/logos";
import logFeaturesImage from "../../assets/LogFeaturesLanding.png";
import {
  ChevronDownIcon,
  CheckIcon,
  StarIcon,
} from "@heroicons/react/20/solid";

// ─── Data ────────────────────────────────────────────────────────────────────

const v2Features = [
  {
    title: "Personalise Insights",
    description:
      "Leverage advanced algorithms & AI to gain personalised insights tailored to your unique data profile.",
  },
  {
    title: "Training Load Analysis",
    description:
      "What training you are doing measured by RPE x Time. Understand your acute and chronic load balance.",
  },
  {
    title: "Recovery Monitoring",
    description:
      "How you are responding to training measured with subjective check-ins and physiological data.",
  },
  {
    title: "Physiological Adaption Analysis",
    description:
      "Track how your cardiorespiratory system adapts over time using wearable data like HRV, resting heart rate, and respiratory rate.",
  },
  {
    title: "Centralise Data in One Place",
    description:
      "All your training, wellness, and wearable data in one place — owned by you. Export, analyse, or delete anytime.",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Triathlete",
    quote:
      "Peak has transformed how I approach training. The readiness score gives me confidence to push hard on the right days and recover when I need to.",
    rating: 5,
  },
  {
    name: "James K.",
    role: "CrossFit Athlete",
    quote:
      "The combination of subjective wellness and wearable data gives me the full picture that wearables alone can't provide. Game changer.",
    rating: 5,
  },
  {
    name: "Coach David L.",
    role: "Athletics Coach",
    quote:
      "Managing 20+ athletes used to be guesswork. Now I can see exactly who needs attention and who's ready to perform at their best.",
    rating: 5,
  },
];

const faqItems = [
  {
    question: "How does the Peak Score work?",
    answer:
      "The Peak Score is a daily readiness signal based on three key inputs: your subjective wellness, training load balance, and physiological data from wearables. It reflects how prepared you are to tolerate training stress and express performance on a given day.",
  },
  {
    question: "Do I need a wearable device?",
    answer:
      "No. Peak works with just your daily training and wellness logs. However, connecting a wearable device (Apple Watch, Garmin, Whoop, Oura, etc.) enhances your insights by adding heart rate, sleep, HRV, and activity data.",
  },
  {
    question: "How long until I see meaningful insights?",
    answer:
      "Your Peak Score starts working after 7 days of consistent logging. The most accurate scoring occurs after 28 days, as the system builds a stronger baseline of your personal data.",
  },
  {
    question: "Can I use Peak as a coach?",
    answer:
      "Yes. The Coach Portal at coach.peak.app is an elite athlete monitoring platform. Connect with your athletes to view their readiness, training load, and wellness data in one dashboard.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Absolutely. Peak is built with privacy-first architecture and industry-leading security protocols. Your data belongs to you — analyse it, export it, or delete it anytime.",
  },
];

const logTypes = [
  {
    name: "Training Load",
    description:
      "Log RPE, duration, and training tags to measure your load over time.",
    icon: "barbell",
  },
  {
    name: "Wellness",
    description:
      "Daily subjective check-in covering mood, sleep quality, stress, recovery, and physical state.",
    icon: "heart",
  },
  {
    name: "Recovery",
    description:
      "Track recovery modalities — ice baths, massage, stretching, compression, and more.",
    icon: "recovery",
  },
  {
    name: "Injuries & Niggles",
    description:
      "Monitor pain levels, impact on training, and injury progression over time.",
    icon: "injury",
  },
  {
    name: "Performance",
    description:
      "Record performance results, expectations, personal bests, and season's bests.",
    icon: "performance",
  },
  {
    name: "Sleep & Health",
    description:
      "Track HRV, resting heart rate, respiratory rate, and sleep duration from wearables.",
    icon: "sleep",
  },
  {
    name: "Activities",
    description:
      "Auto-sync GPS activities, heart rate zones, and calorie data from 11+ wearable brands.",
    icon: "activity",
  },
  {
    name: "Female Cycle",
    description:
      "Track menstrual cycles alongside training to understand how your cycle affects performance.",
    icon: "cycle",
  },
  {
    name: "Notes",
    description:
      "Capture daily reflections, coach feedback, or anything relevant to your training journal.",
    icon: "notes",
  },
];

// ─── FAQ Accordion Item ──────────────────────────────────────────────────────

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-base-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left font-grotesk font-medium text-base-content hover:text-base-content/80 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-expanded={isOpen}
      >
        <span className="pr-4">{question}</span>
        <ChevronDownIcon
          className={`w-5 h-5 shrink-0 text-base-content/40 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 pb-5" : "max-h-0"
        }`}
      >
        <p className="text-base-content/70 font-grotesk leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
}

// ─── Log Type Icon ───────────────────────────────────────────────────────────

function LogTypeIcon({ icon }: { icon: string }) {
  const iconMap: Record<string, string> = {
    barbell: "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5",
    heart:
      "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z",
    recovery:
      "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182",
    injury:
      "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z",
    performance:
      "M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0 1 16.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.04 6.04 0 0 1-4.27 1.772 6.04 6.04 0 0 1-4.27-1.772",
    sleep:
      "M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z",
    activity: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z",
    cycle: "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
    notes:
      "M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10",
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={iconMap[icon] || iconMap.notes}
      />
    </svg>
  );
}

// ─── Main Landing Page ───────────────────────────────────────────────────────

function LandingPage() {
  const navigate = useNavigate();
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  useEffect(() => {
    console.log("Landing page mounted - initializing Intercom...");

    const initIntercom = () => {
      try {
        const anonymousId =
          safeGetItem("intercom_anonymous_landing_id") ||
          `landing_${Date.now()}_${Math.random()
            .toString(36)
            .substring(2, 11)}`;
        safeSetItem("intercom_anonymous_landing_id", anonymousId);

        const intercomConfig = {
          app_id: "wiedyikd",
          hide_default_launcher: false,
          user_id: anonymousId,
          name: "Landing Page Visitor",
          custom_attributes: {
            user_type: "landing_page_visitor",
            page: "landing_page",
          },
        };

        try {
          Intercom(intercomConfig);
          setIntercomLoaded(true);
          console.log("Intercom initialized successfully on landing page");
        } catch (intercomError: any) {
          if (
            intercomError?.name === "SecurityError" ||
            intercomError?.message?.includes("localStorage") ||
            intercomError?.message?.includes("Access is denied")
          ) {
            console.warn(
              "Intercom initialization skipped due to localStorage access restrictions:",
              intercomError,
            );
          } else {
            throw intercomError;
          }
        }
      } catch (error) {
        console.error("Error initializing Intercom:", error);
      }
    };

    initIntercom();

    return () => {
      console.log("Leaving landing page - hiding Intercom...");
      if (window.Intercom) {
        try {
          window.Intercom("hide");
          window.Intercom("update", {
            hide_default_launcher: true,
          });
        } catch (error) {
          console.error("Error hiding Intercom:", error);
        }
      }
    };
  }, []);

  const handleRegister = () => {
    navigate("/onboarding");
  };

  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    } else {
      console.warn("Intercom not loaded yet");
    }
  };

  return (
    <div data-theme="peakx-light">
      <FloatingHeader></FloatingHeader>
      {/* Mobile Hero Section */}
      <section
        id="home"
        className="h-screen md:hidden hero-mobile w-screen flex items-start justify-center pt-32"
      >
        <div className="max-w-sm text-center space-y-2 px-4">
          <div className="flex items-center gap-2 justify-center">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="w-4 h-4 text-warning" />
              ))}
            </div>
            <span className="text-sm text-base-content/60 font-grotesk">
              Trusted by athletes worldwide
            </span>
          </div>

          <h1 className="text-4xl font-bold font-heading leading-tight">
            The training intelligence app for athletes.
          </h1>

          {windowHeight >= 750 && (
            <p className="text-base text-base-content/70 font-grotesk leading-relaxed">
              Peak combines modern sports science and old school wisdom to help
              you{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                reduce injury
              </span>{" "}
              and{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                optimise performance
              </span>
              .
            </p>
          )}

          <div className="flex gap-3 justify-center pt-4">
            <button
              onClick={handleRegister}
              className="px-8 py-3 text-md text-center font-medium bg-primary text-primary-content rounded-3xl shadow-lg transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Get Started
            </button>
            <a
              href="#process"
              className="px-4 py-3 bg-white text-base font-medium border border-base-300 text-base-content rounded-full transition-colors duration-200 font-grotesk hover:bg-base-200 text-center"
            >
              See How It Works
            </a>
          </div>
        </div>
      </section>

      {/* Desktop Hero Section */}
      <section
        id="home-desktop"
        className="hidden md:flex h-screen hero-desktop items-center"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-left space-y-2 w-1/2">
            <div className="flex items-center gap-2 justify-center md:justify-start">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="w-4 h-4 text-warning" />
                ))}
              </div>
              <span className="text-sm text-base-content/60 font-grotesk">
                Trusted by athletes worldwide
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold font-heading leading-tight">
              The training intelligence app for athletes.
            </h1>

            <p className="text-lg md:text-xl text-base-content/70 font-grotesk leading-relaxed max-w-lg mx-auto md:mx-0 pt-4 pb-4">
              Peak combines modern sports science and old school wisdom to help
              you{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                reduce injury
              </span>{" "}
              and{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                optimise performance
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              {windowHeight >= 670 && (
                <div className="flex justify-center">
                  <button
                    onClick={handleRegister}
                    className="px-10 py-3 text-md text-center font-medium bg-primary text-primary-content rounded-3xl shadow-lg transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    Get Started
                  </button>
                </div>
              )}
              <a
                href="#process"
                className="px-8 py-3.5 text-base font-medium border border-base-300 text-base-content rounded-full transition-colors duration-200 font-grotesk hover:bg-base-200 text-center"
              >
                See How It Works
              </a>
            </div>
          </div>
          <div className="w-1/2"></div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="process">
        <Features />
      </section>

      {/* Wearables */}
      <section id="wearables">
        <Logos />
      </section>

      {/* Peak Readiness Score */}
      <section id="science">
        <PeakScore />
      </section>

      {/* What You Can Log */}
      <section id="log-types" className="bg-blue-50 py-24 px-6">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          {/* Header row: text left, image right */}
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-1/2 flex flex-col gap-4">
              <h2 className="text-3xl md:text-5xl font-bold font-grotesk">
                What you can log
              </h2>
              <p className="text-xl text-base-content/70 font-grotesk max-w-lg">
                Everything you need to monitor and understand your body in one
                place. Log daily in under <strong>2 minutes</strong>.
              </p>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <img
                src={logFeaturesImage}
                alt="Peak app log features showing wellness, training load, and recovery screens"
                className="w-full max-w-xs md:max-w-sm rounded-2xl"
                width={384}
                height={768}
              />
            </div>
          </div>

          {/* Log type cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {logTypes.map((log) => (
              <div
                key={log.name}
                className="bg-white rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center shrink-0 text-primary">
                  <LogTypeIcon icon={log.icon} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-base font-semibold font-grotesk">
                    {log.name}
                  </h3>
                  <p className="text-sm text-base-content/60 font-grotesk leading-relaxed">
                    {log.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleRegister}
              className="px-8 py-3.5 text-base font-medium bg-primary text-primary-content rounded-full transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Features Section (from V2) */}
      <section id="features" className="bg-base-100 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              What Peak does with the data
            </h2>
            <p className="text-lg text-base-content/70 font-grotesk max-w-2xl mx-auto">
              Powerful tools designed to help you make better training decisions
              every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {v2Features.slice(0, 2).map((feature, i) => (
              <div key={i} className="bg-base-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold font-grotesk mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-base-content/70 font-grotesk leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {v2Features.slice(2).map((feature, i) => (
              <div key={i} className="bg-base-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold font-grotesk mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-base-content/70 font-grotesk leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section id="coaches">
        <Users />
      </section>

      <section id="network">
        <Network />
      </section>

      {/* Testimonials Section — hidden for now */}
      {/* <section className="bg-base-100 py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              What Athletes Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                className="bg-base-200 rounded-xl p-6 flex flex-col gap-4"
              >
                <div className="flex" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <StarIcon key={j} className="w-4 h-4 text-warning" />
                  ))}
                </div>
                <p className="text-base-content/80 font-grotesk leading-relaxed flex-1">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-base-300 flex items-center justify-center">
                    <span className="text-sm font-bold font-grotesk text-base-content/60">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold font-grotesk">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-base-content/60 font-grotesk">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ Section — hidden for now */}
      {/* <section className="bg-base-200 py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              FAQ
            </h2>
          </div>

          <div>
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="bg-base-100 py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center p-10 md:p-16 bg-base-300 rounded-2xl">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              Ready to train smarter?
            </h2>
            <p className="text-lg text-base-content/70 font-grotesk mb-8 max-w-xl mx-auto">
              Join athletes worldwide who are using Peak to make better training
              decisions every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRegister}
                className="px-10 py-3.5 text-base font-medium bg-primary text-primary-content rounded-full transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Get Started Free
              </button>
              <button
                onClick={openChat}
                disabled={!intercomLoaded}
                className={`px-10 py-3.5 text-base font-medium rounded-full transition-colors duration-200 font-grotesk border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                  intercomLoaded
                    ? "bg-base-100 text-base-content border-base-300 hover:bg-base-200"
                    : "bg-base-300 text-base-content/60 border-base-300 cursor-not-allowed"
                }`}
              >
                {intercomLoaded ? "Chat with Us" : "Loading chat..."}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-base-100 border-t border-base-300">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col items-center md:items-start gap-4">
              <div className="text-3xl font-groteskLogo font-bold">
                <h1 className="text-3xl font-groteskLogo">
                  PEAK
                  <span className="font-bold text-primary -ml-1">^</span>
                </h1>
              </div>
              <p className="text-base-content/70 text-sm">
                &copy; {new Date().getFullYear()} PEAK APP. All rights reserved.
                A project by Equil Labs Pty Ltd.
              </p>
            </div>

            <div className="flex gap-6 text-sm">
              <a
                href="https://help.peak.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Help & Support
              </a>
              <a
                href="/privacy-policy"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
